<script lang="ts">
  import {
    CONTRACT_PIPELINE_LABELS,
    CONTRACT_STATUS_LABELS,
    type ContractListQuery,
  } from "@shared/types/contract";
  import type { Contract } from "@domain/contract/model";
  import type { AssignableAccount } from "@shared/types/account";
  import AccountMultiSelect from "@shared/ui/AccountMultiSelect.svelte";
  import { formatDate, formatDateTime } from "@shared/util/format";

  type SortField = NonNullable<ContractListQuery["sort_by"]>;
  type SortDir = NonNullable<ContractListQuery["sort_dir"]>;

  interface LawyerOption {
    id: string;
    label: string;
  }

  interface Props {
    contracts: Contract[];
    sortBy: SortField;
    sortDir: SortDir;
    onSort: (field: SortField) => void;
    hideFirm?: boolean;
    canEdit?: boolean;
    /** アーカイブ済み一覧として表示する。操作は「復元」のみ。 */
    archived?: boolean;
    lawyerOptions?: LawyerOption[];
    /// 担当者マスタ (ACC-4)。assignee_account_ids をチップ表示するために使う。
    /// 契約の担当者編集は編集モーダル側で行うため、本テーブルでは表示のみ。
    accountOptions?: AssignableAccount[];
    onEdit?: (c: Contract) => void;
    onTransition?: (c: Contract) => void;
    onArchive?: (c: Contract) => void;
    onRestore?: (c: Contract) => void;
  }

  let {
    contracts,
    sortBy,
    sortDir,
    onSort,
    hideFirm = false,
    canEdit = false,
    archived = false,
    lawyerOptions = [],
    accountOptions = [],
    onEdit,
    onTransition,
    onArchive,
    onRestore,
  }: Props = $props();

  interface Column {
    key: SortField | null;
    label: string;
  }

  let columns = $derived<Column[]>(
    hideFirm
      ? archived
        ? [
            { key: null, label: "取引名" },
            { key: null, label: "契約期間" },
            { key: "status", label: "契約状態" },
            { key: null, label: "アーカイブ日時" },
            { key: null, label: "アーカイブ理由" },
            { key: null, label: "操作" },
          ]
        : [
            { key: null, label: "取引名" },
            { key: null, label: "パイプライン" },
            { key: null, label: "MRR(月額)" },
            { key: null, label: "契約期間" },
            { key: null, label: "自動更新" },
            { key: null, label: "アカウント数" },
            { key: null, label: "契約対象弁護士" },
            { key: "status", label: "契約状態" },
            { key: null, label: "担当者" },
            { key: null, label: "操作" },
          ]
      : archived
        ? [
            { key: null, label: "契約種別" },
            { key: null, label: "事務所" },
            { key: "status", label: "ステータス" },
            { key: null, label: "アーカイブ日時" },
            { key: null, label: "アーカイブ理由" },
            { key: null, label: "操作" },
          ]
        : [
            { key: null, label: "契約種別" },
            { key: null, label: "事務所" },
            { key: "status", label: "ステータス" },
            { key: "signed_on", label: "締結日" },
            { key: "ends_on", label: "終了日" },
            { key: "updated_at", label: "更新日時" },
            { key: null, label: "操作" },
          ],
  );

  function indicator(field: SortField | null): string {
    if (field === null || field !== sortBy) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  function formatYen(value: string | null): string {
    if (value === null || value === "") return "—";
    const n = Number(value);
    if (Number.isNaN(n)) return value;
    return `¥${n.toLocaleString("ja-JP")}`;
  }

  function dealNameLabel(c: Contract): string {
    return c.deal_name ?? "(取引名未設定)";
  }

  function lawyerLabel(c: Contract): string {
    if (!c.main_interest_lawyer_id) return "—";
    return (
      lawyerOptions.find((l) => l.id === c.main_interest_lawyer_id)?.label ??
      c.main_interest_lawyer_id
    );
  }

  function pipelineLabel(c: Contract): string {
    return c.pipeline ? CONTRACT_PIPELINE_LABELS[c.pipeline] : "—";
  }

  /** 契約期間表示。 starts_on / ends_on の組み合わせで「開始 - 終了」 / 「期限なし」 / 「—」。 */
  function periodLabel(c: Contract): string {
    const s = c.starts_on ? formatDate(c.starts_on) : null;
    const e = c.ends_on ? formatDate(c.ends_on) : null;
    if (s && e) return `${s} 〜 ${e}`;
    if (s && !e) return `${s} 〜 期限なし`;
    if (!s && e) return `〜 ${e}`;
    return "—";
  }

  function archivedAtLabel(c: Contract): string {
    return c.archived_at ? formatDateTime(c.archived_at) : "—";
  }
</script>

{#snippet normalActions(c: Contract)}
  {#if onEdit}
    <button type="button" disabled={!canEdit} onclick={() => onEdit?.(c)}>
      編集
    </button>
  {/if}
  {#if onTransition}
    <button
      type="button"
      disabled={!canEdit || c.status === "canceled" || c.status === "expired"}
      onclick={() => onTransition?.(c)}
    >
      遷移
    </button>
  {/if}
  {#if onArchive}
    <button
      type="button"
      class="warn"
      disabled={!canEdit}
      onclick={() => onArchive?.(c)}
    >
      アーカイブ
    </button>
  {/if}
{/snippet}

{#snippet restoreActions(c: Contract)}
  {#if onRestore}
    <button
      type="button"
      class="primary"
      disabled={!canEdit}
      onclick={() => onRestore?.(c)}
    >
      復元
    </button>
  {/if}
{/snippet}

<table>
  <thead>
    <tr>
      {#each columns as col (col.label)}
        {#if col.key}
          <th>
            <button type="button" onclick={() => onSort(col.key as SortField)}>
              {col.label}{indicator(col.key)}
            </button>
          </th>
        {:else}
          <th>{col.label}</th>
        {/if}
      {/each}
    </tr>
  </thead>
  <tbody>
    {#each contracts as c (c.id)}
      <tr>
        {#if hideFirm}
          {#if archived}
            <td class="primary">
              <div class="deal-name-cell">
                <span>{dealNameLabel(c)}</span>
                {#if c.line_item_count > 0}
                  <span class="line-item-count">商品 {c.line_item_count}件</span>
                {/if}
                {#if c.party.is_individual_display}
                  <span class="individual-badge">個人契約</span>
                {/if}
              </div>
            </td>
            <td>{periodLabel(c)}</td>
            <td>
              <span class="status status-{c.status}">
                {CONTRACT_STATUS_LABELS[c.status]}
              </span>
            </td>
            <td class="timestamp">{archivedAtLabel(c)}</td>
            <td>{c.archive_reason ?? "—"}</td>
            <td class="actions">{@render restoreActions(c)}</td>
          {:else}
            <td class="primary">
              <div class="deal-name-cell">
                <span>{dealNameLabel(c)}</span>
                {#if c.line_item_count > 0}
                  <span class="line-item-count">商品 {c.line_item_count}件</span>
                {/if}
                {#if c.party.is_individual_display}
                  <span class="individual-badge">個人契約</span>
                {/if}
              </div>
            </td>
            <td>{pipelineLabel(c)}</td>
            <td>{formatYen(c.monthly_amount)}</td>
            <td>{periodLabel(c)}</td>
            <td>
              <span class="renewal" class:renewal-on={c.auto_renewal}>
                {c.auto_renewal ? "自動更新あり" : "自動更新なし"}
              </span>
            </td>
            <td>{c.active_user_count ?? "—"}</td>
            <td>{lawyerLabel(c)}</td>
            <td>
              <span class="status status-{c.status}">
                {CONTRACT_STATUS_LABELS[c.status]}
              </span>
            </td>
            <td class="assignees">
              <AccountMultiSelect
                value={c.assignee_account_ids}
                accounts={accountOptions}
                disabled
              />
            </td>
            <td class="actions">{@render normalActions(c)}</td>
          {/if}
        {:else if archived}
          <td class="primary">
            <div class="deal-name-cell">
              <span>{c.contract_type ?? "(未指定)"}</span>
              {#if c.party.is_individual_display}
                <span class="individual-badge">個人契約</span>
              {/if}
            </div>
          </td>
          <td>
            <a href={`/firms/${c.firm_id}`}>{c.firm_id}</a>
          </td>
          <td>
            <span class="status status-{c.status}">
              {CONTRACT_STATUS_LABELS[c.status]}
            </span>
          </td>
          <td class="timestamp">{archivedAtLabel(c)}</td>
          <td>{c.archive_reason ?? "—"}</td>
          <td class="actions">{@render restoreActions(c)}</td>
        {:else}
          <td class="primary">
            <div class="deal-name-cell">
              <span>{c.contract_type ?? "(未指定)"}</span>
              {#if c.party.is_individual_display}
                <span class="individual-badge">個人契約</span>
              {/if}
            </div>
          </td>
          <td>
            <a href={`/firms/${c.firm_id}`}>{c.firm_id}</a>
          </td>
          <td>
            <span class="status status-{c.status}">
              {CONTRACT_STATUS_LABELS[c.status]}
            </span>
          </td>
          <td>{formatDate(c.signed_on)}</td>
          <td>{formatDate(c.ends_on)}</td>
          <td class="timestamp">{formatDateTime(c.updated_at)}</td>
          <td class="actions">{@render normalActions(c)}</td>
        {/if}
      </tr>
    {/each}
    {#if contracts.length === 0}
      <tr>
        <td colspan={columns.length} class="empty">
          {archived ? "アーカイブ済み契約はありません" : "契約はありません"}
        </td>
      </tr>
    {/if}
  </tbody>
</table>

<style>
  table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
  }
  thead {
    background: #f9fafb;
  }
  th {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.75rem;
    color: #4b5563;
    font-weight: 500;
  }
  th button {
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.875rem;
    color: #111827;
    vertical-align: top;
  }
  td.primary {
    font-weight: 500;
  }
  .deal-name-cell {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .line-item-count {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: #eef2ff;
    color: #3730a3;
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .individual-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: #fdf2f8;
    color: #9d174d;
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
  }
  td.timestamp {
    color: #6b7280;
    font-size: 0.75rem;
  }
  td.empty {
    color: #9ca3af;
    text-align: center;
    padding: 24px;
  }
  td.actions {
    white-space: nowrap;
  }
  td.actions button {
    margin-right: 4px;
  }
  .status {
    padding: 1px 6px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #374151;
    font-size: 0.75rem;
  }
  .status-draft {
    background: #f3f4f6;
    color: #374151;
  }
  .status-active {
    background: #dcfce7;
    color: #166534;
  }
  .status-canceled {
    background: #fee2e2;
    color: #991b1b;
  }
  .status-expired {
    background: #fef3c7;
    color: #92400e;
  }
  .renewal {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .renewal.renewal-on {
    background: #d1fae5;
    color: #065f46;
  }
  button {
    padding: 4px 8px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
    font-size: 0.75rem;
  }
  button.warn {
    border-color: #b45309;
    color: #b45309;
  }
  button.primary {
    border-color: #2563eb;
    color: #2563eb;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
